/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button, FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material/';

import { BasicRoutes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { createForgotPageStyles } from '../../ForgotPage.styles';
import { ForgotApiValidationErrors, ForgotPageFormValues, ResetApiErrorForField } from '../../ForgotPage.types';

type ForgotFormProps = FormikProps<ForgotPageFormValues> & {
  isFetching: boolean;
  resetApiError: ResetApiErrorForField;
  apiErrors: ForgotApiValidationErrors;
};

export const ForgotForm = memo<ForgotFormProps>(props => {
  const { t } = useTranslation(Namespaces.Login);
  const classes = createForgotPageStyles();
  const formClasses = useFormStyles();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    setFieldTouched,
    isFetching,
    apiErrors,
    resetApiError,
    handleBlur,
  } = props;
  const navigate = useNavigate();

  const change = (name: keyof ForgotPageFormValues, e: ChangeEvent) => {
    e.persist();
    handleChange(e);
    resetApiError(name);
    void setFieldTouched(name, true, false);
  };

  const handleChangeBackToLogin = () => {
    void navigate(BasicRoutes.LOGIN);
  };

  const hasError = (name: keyof ForgotPageFormValues): boolean | undefined => {
    return touched[name] && !!(errors[name] || apiErrors[name]);
  };

  const getError = (name: keyof ForgotPageFormValues): string | undefined => {
    const error = errors[name] || apiErrors[name];
    if (error) {
      return t(`validation.${name}.${error}`);
    }
    return undefined;
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit}>
      <FormControl className={cx(formClasses.formControl)} error={hasError('email')} variant="outlined">
        <InputLabel htmlFor="email">{t(`${Namespaces.Entities}:user.email.label`)}</InputLabel>
        <OutlinedInput
          id="email"
          name="email"
          value={values.email}
          onChange={change.bind(null, 'email')}
          label={t(`${Namespaces.Entities}:user.email.label`)}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
          type="email"
        />
        {hasError('email') && <FormHelperText>{getError('email')}</FormHelperText>}
      </FormControl>
      <Grid container className={classes.formFooter}>
        <Grid item xs>
          <Button size="small" onClick={() => handleChangeBackToLogin()}>
            {t('page.forgot_password.back_to_login')}
          </Button>
        </Grid>
        <Grid item>
          <Button type="submit" color="primary" variant="contained" size="small" disabled={isFetching}>
            {t('page.forgot_password.send_reset_email')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
});
